// frontend/src/pages/usecases/ProjectSwitch.jsx
// Use-case landing page: Project context switching

import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material';
import { useThemeMode } from '../../contexts/ThemeContext';
import { Link } from 'react-router-dom';

const CHROME_URL =
  'https://chromewebstore.google.com/detail/blackmount-%E2%80%93-save-restore/fpchhlhegjjjkfnhcdgfjcbinhhmfccc';

const STEPS = [
  {
    num: '1',
    title: 'Save your current project\'s tabs as a session',
    desc: 'Docs, Jira, Figma, Slack, dashboards — everything captured in one click.',
  },
  {
    num: '2',
    title: 'Switch to another project',
    desc: 'Open a clean browser or restore a different saved session. No tab clutter.',
  },
  {
    num: '3',
    title: 'Restore the first project later with one click',
    desc: 'Come back to the exact set of tabs you had open. No re-finding, no re-loading.',
  },
];

const ProjectSwitch = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { darkMode, toggleDarkMode } = useThemeMode();

  return (
    <>
      <Helmet>
        <title>Switch Between Projects Without Losing Tabs — Save One Context, Open Another | Blackmount</title>
        <meta
          name="description"
          content="Juggling multiple projects? Save each project's tabs as a session and switch between them with one click. No more losing your place."
        />
        <link rel="canonical" href="https://app.blackmount.ai/use-cases/project-switch" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Switch Between Projects Without Losing Tabs | Blackmount" />
        <meta property="og:description" content="Save each project's tabs as a session and switch contexts with one click. No more losing your place." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://app.blackmount.ai/use-cases/project-switch" />
        <meta property="og:image" content="https://app.blackmount.ai/og-image.png" />
        <meta property="og:site_name" content="Blackmount" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Switch Between Projects Without Losing Tabs | Blackmount" />
        <meta name="twitter:description" content="Save each project's tabs as a session and switch contexts with one click. No more losing your place." />
        <meta name="twitter:image" content="https://app.blackmount.ai/og-image.png" />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Top Bar */}
        <AppBar
          position="static"
          elevation={0}
          sx={{ bgcolor: 'background.paper', borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Toolbar>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <Box
                component={Link}
                to="/"
                sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
              >
                <Box
                  component="img"
                  src="/blackmount-logo.jpg"
                  alt="Blackmount"
                  sx={{ height: 32, width: 'auto', borderRadius: 0.5 }}
                />
              </Box>
            </Box>
            <IconButton color="inherit" onClick={toggleDarkMode}>
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Hero */}
        <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            component="h1"
            fontWeight={700}
            sx={{ mb: 3, color: 'text.primary' }}
          >
            Switch Between Projects Without Losing Tabs
          </Typography>

          <Typography
            variant={isMobile ? 'body1' : 'h6'}
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 620, mx: 'auto', lineHeight: 1.6 }}
          >
            You're juggling 3 projects. Each has its own set of tabs — docs, Jira, Figma, Slack.
            Switching means losing your place.
          </Typography>

          {/* Screenshot placeholder */}
          <Box
            sx={{
              mx: 'auto',
              mb: 8,
              maxWidth: 600,
              height: 260,
              borderRadius: 3,
              border: `2px dashed ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme.palette.action.hover,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Screenshot coming soon
            </Typography>
          </Box>

          {/* How it works */}
          <Typography variant="h5" fontWeight={600} sx={{ mb: 4, color: 'text.primary' }}>
            How it works
          </Typography>

          <Stack spacing={4} sx={{ maxWidth: 520, mx: 'auto', textAlign: 'left', mb: 8 }}>
            {STEPS.map((step) => (
              <Box key={step.num} sx={{ display: 'flex', gap: 2 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    flexShrink: 0,
                  }}
                >
                  {step.num}
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>

          {/* CTA */}
          <Button
            variant="contained"
            size="large"
            startIcon={<ExtensionIcon />}
            onClick={() => window.open(CHROME_URL, '_blank', 'noopener,noreferrer')}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 3,
              textTransform: 'none',
            }}
          >
            Add to Chrome — Free
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default ProjectSwitch;
